import { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import LeafLogo from '../components/LeafLogo';
import styles from './LoginPage.module.css';

export default function LoginPage() {
  const { login } = useAuth();

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const [showReset,      setShowReset]      = useState(false);
  const [resetEmail,     setResetEmail]     = useState('');
  const [resetError,     setResetError]     = useState('');
  const [resetSent,      setResetSent]      = useState(false);
  const [resetLoading,   setResetLoading]   = useState(false);

  const handleForgotPassword = async () => {
    setResetError('');
    if (!resetEmail) { setResetError('Please enter your email address.'); return; }
    setResetLoading(true);
    try {
      const actionCodeSettings = {
        url: window.location.hostname === 'localhost'
          ? 'http://localhost:3000/reset-password'
          : 'https://topjuanwscms.netlify.app/reset-password',
        handleCodeInApp: true,
      };
      await sendPasswordResetEmail(auth, resetEmail, actionCodeSettings);
      setResetSent(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setResetError('No account found with that email.');
      } else if (err.code === 'auth/invalid-email') {
        setResetError('Please enter a valid email address.');
      } else {
        setResetError('Something went wrong. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const closeReset = () => {
    setShowReset(false);
    setResetEmail('');
    setResetError('');
    setResetSent(false);
  };

  const handleLogin = async () => {
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.message);
      setLoading(false);
    }
    // On success, AuthContext updates `user` → App.jsx redirects automatically
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div className={styles.root}>

      {/* ── Left Panel ── */}
      <div className={styles.left}>
        <div className={styles.logoWrap}>
          <div className={styles.logoCircle}>
            <LeafLogo size={80} color="#FFFFFF" bgColor="#7B3F1E" />
          </div>
          <div className={styles.brandName}>
            Juan Café
            <span>Supply Chain Management</span>
          </div>
        </div>
        <p className={styles.tagline}>
          Streamlining supply ordering across all branches of Top Juan Franchising Inc.
        </p>
        <div className={styles.decorCircle1} />
        <div className={styles.decorCircle2} />
      </div>

      {/* ── Right Panel ── */}
      <div className={styles.right}>
        <div className={styles.formHead}>
          <h2>Welcome back</h2>
          <p>Sign in to your account</p>
        </div>

        {error && (
          <div className={styles.errorBanner}>
            <span>⚠</span> {error}
          </div>
        )}

        {/* Email */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="email">Email</label>
          <input
            id="email"
            className={`${styles.input} ${error ? styles.inputError : ''}`}
            type="email"
            placeholder="e.g. fairview@topjuan.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            autoComplete="email"
          />
        </div>

        {/* Password */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="password">Password</label>
          <input
            id="password"
            className={`${styles.input} ${error ? styles.inputError : ''}`}
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="current-password"
          />
        </div>

        <button className={styles.forgotLink} type="button" onClick={() => setShowReset(true)}>
          Forgot password?
        </button>

        {/* ── Forgot Password Modal ── */}
        {showReset && (
          <div className={styles.modalOverlay} onClick={closeReset}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <button className={styles.modalClose} onClick={closeReset} type="button">✕</button>

              {resetSent ? (
                <>
                  <h3 className={styles.modalTitle}>Check your inbox</h3>
                  <p className={styles.modalBody}>
                    A password reset link has been sent to <strong>{resetEmail}</strong>.<br />
                    Click the link in the email to set a new password.
                  </p>
                  <button className={styles.loginBtn} onClick={closeReset} type="button">
                    Back to Sign In
                  </button>
                </>
              ) : (
                <>
                  <h3 className={styles.modalTitle}>Reset your password</h3>
                  <p className={styles.modalBody}>Enter your account email and we'll send you a reset link.</p>

                  {resetError && (
                    <div className={styles.errorBanner}>
                      <span>⚠</span> {resetError}
                    </div>
                  )}

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="reset-email">Email</label>
                    <input
                      id="reset-email"
                      className={`${styles.input} ${resetError ? styles.inputError : ''}`}
                      type="email"
                      placeholder="e.g. fairview@topjuan.com"
                      value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
                      autoFocus
                    />
                  </div>

                  <button
                    className={styles.loginBtn}
                    onClick={handleForgotPassword}
                    disabled={resetLoading}
                    type="button"
                  >
                    {resetLoading ? 'Sending…' : 'Send Reset Link'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <button
          className={styles.loginBtn}
          onClick={handleLogin}
          disabled={loading}
          type="button"
        >
          {loading ? 'Signing in…' : 'Log In'}
        </button>

        <p className={styles.demoHint}>
          <strong>Demo credentials</strong><br />
          Admin: admin@topjuan.com / Admin@2025!<br />
          Supplier: supplier@topjuan.com / Supplier@2025!<br />
          Branch: fairview@topjuan.com / Branch@2025!
        </p>
      </div>
    </div>
  );
}
